import { useContext, useEffect } from 'react';

import { base64, hex } from '@scure/base';
import { Transaction } from '@scure/btc-signer';
import axios from 'axios';
import { create } from 'zustand';

import { BtcLikeWallet, SignPsbtOptions } from '@/networks/btc-like/common/types';

import { convertSignInputsToInputType } from '../../common';
import { NETWORK } from '../constants';
import { LitecoinContext } from '../providers/LitecoinProvider';

type PsbtOptions = {
  autoFinalized?: boolean;
  toSignInputs?: {
    index: number;
    address: string;
    sighashTypes?: number[];
    disableTweakSigner?: boolean;
  }[];
};

type CurrentAccountStore = {
  address: string | null;
  publicKey: string | null;
  setAccount: (address: string | null, publicKey: string | null) => void;
};

export const useCurrentAddressStore = create<CurrentAccountStore>((set) => {
  return {
    address: null,
    publicKey: null,
    setAccount: (address, publicKey) => {
      set({ address, publicKey });
    },
  };
});

export const useLtcWallet = (): BtcLikeWallet => {
  const ctx = useContext(LitecoinContext);

  if (!ctx) {
    throw new Error('useLtcWallet must be used within LitecoinProvider');
  }

  const { address, publicKey, setAccount } = useCurrentAddressStore();
  const { address: walletAddress, connect, disconnect, getPublicKey, isConnected } = ctx;

  useEffect(() => {
    if (walletAddress) {
      (async () => {
        const publicKey = await getPublicKey();
        if (!publicKey) {
          throw new Error('Public key not found');
        }

        setAccount(walletAddress, publicKey);
      })().catch((error) => {
        console.error(error);
      });
    }
  }, [walletAddress]);

  const signPsbt = async (
    psbtBase64: string,
    { broadcast, allowedSignHash, finalizeSignalInput, sighTx }: SignPsbtOptions
  ) => {
    if (!address) {
      throw new Error('Wallet is not connected');
    }

    const bytePsbt = base64.decode(psbtBase64);

    let hexPsbt: string;

    if (finalizeSignalInput) {
      const txInitial = Transaction.fromPSBT(bytePsbt, {
        allowUnknownOutputs: true,
        disableScriptCheck: true,
      });

      txInitial.finalizeIdx(0);

      hexPsbt = hex.encode(txInitial.toPSBT(0));
    } else {
      hexPsbt = hex.encode(bytePsbt);
    }

    const options: PsbtOptions = {
      autoFinalized: finalizeSignalInput,
      toSignInputs: convertSignInputsToInputType({ [address]: sighTx }, allowedSignHash, true),
    };

    const res = await ctx.signPsbt(hexPsbt, options);

    const signedBase64Psbt = Buffer.from(res, 'hex').toString('base64');

    if (!broadcast) {
      return {
        signedBase64Psbt,
        txId: null,
      };
    }

    const signedTx = Transaction.fromPSBT(hex.decode(res), {
      allowUnknownOutputs: true,
      disableScriptCheck: true,
    });

    if (!signedTx.isFinal) signedTx.finalize();

    const embedUrl = NETWORK.ExplorerUrl.concat('/api/tx');

    const response = await axios.post<string>(embedUrl, signedTx.hex);

    return {
      signedBase64Psbt,
      txId: response.data,
    };
  };

  return {
    isConnected,
    availableAddresses: ctx.address ? [ctx.address] : [],
    address,
    publicKey,
    setAddress: () => null,
    disconnect,
    connect,
    signPsbt,
  };
};
